import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { RootState } from "../../../app/store";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import { DataNode } from "../../../interface/Trends";
import {
  handleDownloadColorAnalysis,
  setLevel1Data,
} from "../../../utils/trendSidebarGraphUtils/colorAnalysisUtils";
import ColorAnalysisGraph from "./ColorAnalysisGraph";
import { ReactComponent as Download } from "../../../assets/icons/download.svg";
import {
  getStoredColorForLevel2,
  getTrendFiltersFromSession,
} from "../../../utils/trendSidebarGraphUtils/graphHelperUtils";
import { getKeyFromValueForTrendQueryType } from "../../../utils/commonUtils";

interface ExpandViewColorAnalysisProps {
  isSidebarOpen?: boolean;
}

const ExpandViewColorAnalysis: React.FC<ExpandViewColorAnalysisProps> = ({
  isSidebarOpen,
}) => {
  const navigate = useNavigate();
  const heightInPx = window.innerHeight - 140;
  const [loading, setLoading] = useState(false);
  const [colorAnalysisData, setColorAnalysisData] = useState<DataNode | null>(
    null
  );
  const [downloadLoading, setDownloadLoading] = useState(false);
  const user = useSelector((state: RootState) => state.user.userInfo);
  const [colorForLevel2, setColorForLevel2] = useState<string>(() =>
    getStoredColorForLevel2()
  );
  const [dataForLevel2, setDataForLevel2] = useState<DataNode | null>(null);
  const [trendFilterType, setTrendFilterType] =
    useState<string>("Most Popular");

  const fetchColorAnalysisData = async () => {
    try {
      setLoading(true);
      const trendFilters = getTrendFiltersFromSession();
      let newFilterType = trendFilterType;
      if (trendFilters?.query_type) {
        newFilterType = getKeyFromValueForTrendQueryType(
          trendFilters.query_type
        );
        setTrendFilterType(newFilterType);
      }
      await setLevel1Data(user, newFilterType, setColorAnalysisData);
      const restoredColorForLevel2 = getStoredColorForLevel2();
      if (restoredColorForLevel2) {
        setColorForLevel2(restoredColorForLevel2);
      }
    } catch (error) {
      console.error("Error Fetching color analysis data", error);
      if ((error as AxiosError)?.response?.status !== 400) {
        toast.error("Error Fetching color analysis data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchColorAnalysisData();
  }, [trendFilterType]);

  if (colorAnalysisData && Object.keys(colorAnalysisData).length === 0) {
    return <div className="my-10">No Color Analysis Data</div>;
  }

  return (
    <div
      className={`bg-white fixed h-[calc(100vh-80px)] ml-2 ${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} transition-all duration-500 ease-in-out`}
    >
      <div className="flex flex-row justify-between mb-4">
        <div className="flex flex-row mt-6">
          <div>
            <LeftArrow
              className="text-lg ml-4 mt-4 cursor-pointer"
              onClick={() => navigate("/designer-page")}
            />
          </div>
          <div className="transition-all  duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-4 mt-2.5 text-secondary ">
            Color Analysis
          </div>
        </div>
        <div className="mt-2 mr-4 flex items-end">
          <div>
            <button
              onClick={() =>
                handleDownloadColorAnalysis(
                  colorAnalysisData,
                  dataForLevel2,
                  trendFilterType,
                  setDownloadLoading
                )
              }
              disabled={downloadLoading}
              title="Download Color Analysis Report"
              className="flex items-center gap-2 mr-5 mt-1 border border-gray-200 rounded-md px-3 py-1.5 bg-white text-secondary hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {downloadLoading ? (
                <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
              ) : (
                <Download width={20} height={20} />
              )}
            </button>
          </div>
          <div>
            <div className="text-sm">Filter By</div>
            <div className="w-40">
              <div className="mt-0 block w-full pl-3 pr-3 py-1 text-left text-base border border-default rounded-lg bg-gray-50">
                <span className="text-secondary text-xs capitalize">
                  {trendFilterType || "Select type"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-h-[calc(100vh-170px)] overflow-y-auto px-8 pt-12">
        {!colorAnalysisData || loading ? (
          <div className="flex justify-center w-full">
            <Skeleton width={heightInPx} height={heightInPx} circle={true} />
          </div>
        ) : (
          <div className="m-auto">
            <ColorAnalysisGraph
              width={heightInPx}
              data={colorAnalysisData}
              colorForLevel2={colorForLevel2}
              setColorForLevel2={setColorForLevel2}
              dataForLevel2={dataForLevel2}
              setDataForLevel2={setDataForLevel2}
              trendFilterType={trendFilterType}
              expandedView={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpandViewColorAnalysis;
